import { useState } from "react";
import {
  ChevronLeft, ChevronRight, Linkedin, Facebook, Instagram,
  Clock, CheckCircle2, AlertCircle, XCircle, Eye
} from "lucide-react";

const PLATFORM_CONFIG = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877f2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#e1306c" },
};

const STATUS_DOT = {
  brouillon: "bg-slate-400",
  en_attente_validation: "bg-amber-400",
  valide: "bg-blue-400",
  planifie: "bg-blue-500",
  publie: "bg-green-500",
  rejete: "bg-red-400",
};

function daysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function firstDow(y, m) { return (new Date(y, m, 1).getDay() + 6) % 7; }

export function MarketingCalendar({ calDate, setCalDate, calData, onClickDate, onClickPost }) {
  const [hoveredPost, setHoveredPost] = useState(null);

  const prevMonth = () => setCalDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setCalDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const calYear = calDate.getFullYear();
  const calMonth = calDate.getMonth();
  const calDays = daysInMonth(calYear, calMonth);
  const calStart = firstDow(calYear, calMonth);
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="calendar-tab">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="cal-prev-month">
          <ChevronLeft className="w-4 h-4 text-slate-400" />
        </button>
        <h3 className="text-sm font-bold capitalize">
          {calDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
        </h3>
        <button onClick={nextMonth} className="p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="cal-next-month">
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {/* Grid */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-white/5">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map(d => (
            <div key={d} className="px-2 py-2 text-[10px] text-slate-500 text-center uppercase font-bold">{d}</div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7">
          {/* Empty cells before month start */}
          {Array.from({ length: calStart }).map((_, i) => (
            <div key={`e-${i}`} className="min-h-[90px] border-r border-b border-white/[0.03]" />
          ))}

          {/* Actual days */}
          {Array.from({ length: calDays }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const dayPosts = calData.by_date?.[dateStr] || [];
            const isToday = dateStr === todayStr;

            return (
              <div
                key={day}
                className={`min-h-[90px] border-r border-b border-white/[0.03] p-1 cursor-pointer hover:bg-white/[0.02] transition-colors ${isToday ? "bg-[#10b981]/5" : ""}`}
                onClick={() => onClickDate(dateStr)}
                data-testid={`cal-day-${dateStr}`}
              >
                <div className={`text-[10px] font-bold mb-1 flex items-center justify-between ${isToday ? "text-[#10b981]" : "text-slate-500"}`}>
                  <span>{day}</span>
                  {dayPosts.length > 0 && (
                    <span className="text-[8px] bg-white/5 px-1 rounded">{dayPosts.length}</span>
                  )}
                </div>

                {dayPosts.slice(0, 3).map(p => {
                  const mainPlatform = (p.platforms || [p.platform])[0] || "linkedin";
                  const cfg = PLATFORM_CONFIG[mainPlatform] || PLATFORM_CONFIG.linkedin;
                  const Icon = cfg.icon;
                  const statusDot = STATUS_DOT[p.status] || STATUS_DOT.brouillon;

                  return (
                    <div
                      key={p.post_id}
                      onClick={e => { e.stopPropagation(); onClickPost(p); }}
                      onMouseEnter={() => setHoveredPost(p.post_id)}
                      onMouseLeave={() => setHoveredPost(null)}
                      className="text-[8px] px-1.5 py-0.5 rounded mb-0.5 truncate cursor-pointer hover:opacity-90 transition-opacity flex items-center gap-1"
                      style={{ background: `${cfg.color}12`, color: cfg.color }}
                      data-testid={`cal-post-${p.post_id}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDot}`} />
                      {/* Show platform icons for multi-platform */}
                      {(p.platforms || []).length > 1 ? (
                        <span className="flex items-center gap-0.5">
                          {(p.platforms || []).slice(0, 3).map(pl => {
                            const Ic = (PLATFORM_CONFIG[pl] || PLATFORM_CONFIG.linkedin).icon;
                            return <Ic key={pl} className="w-2 h-2" />;
                          })}
                        </span>
                      ) : (
                        <Icon className="w-2 h-2 flex-shrink-0" />
                      )}
                      <span className="truncate">{(p.content || "").substring(0, 20)}</span>
                    </div>
                  );
                })}
                {dayPosts.length > 3 && (
                  <div className="text-[8px] text-slate-500 pl-1">+{dayPosts.length - 3}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3 px-1">
        {[
          { label: "Brouillon", cls: "bg-slate-400" },
          { label: "En attente", cls: "bg-amber-400" },
          { label: "Valide", cls: "bg-blue-400" },
          { label: "Planifie", cls: "bg-blue-500" },
          { label: "Publie", cls: "bg-green-500" },
          { label: "Rejete", cls: "bg-red-400" },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${l.cls}`} />
            <span className="text-[9px] text-slate-500">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
